import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Banknote, BriefcaseBusiness, CalendarCheck, CalendarDays, CalendarX, CircleCheckBig, CircleX, Clock, FileDown, FileText,
  Hourglass, MessageSquarePlus, Printer, RefreshCw, Save, Search, Send, ShieldAlert, Timer,
} from 'lucide-react'
import * as hr from '../api'
import Loader from '../components/Loader'
import { useHR } from '../HRContext'
import Modal from '../components/Modal'
import { download, fmtMoney, fmtNumber, toCSV } from '../format'
import { Chip, Kpi, PAY_TONE, STATUS_TONE, Table, YesNo, money, num } from './missed/shared'

// Missed Days > Employee History (Stephanet's MissedDaysHistory): Employee Attendance History & Payroll Impact Center — employee list on
// the left, the selected employee's history on the right; statement actions are kept on missed_day_statement_actions

const HIST_TONE = { Normal: 'green', 'Pending HR Review': 'yellow', 'Deduction Pending': 'orange', 'Query Required': 'purple', 'High Risk': 'red', 'Payroll Posted': 'teal' }
const RISK_TONE = { Low: 'green', Medium: 'yellow', High: 'orange', Critical: 'darkred' }
const WARN_TONE = { Normal: 'green', Warning: 'yellow', 'HR Query Required': 'red', 'Management Review': 'darkred' }
const TABS = ['Overview', 'Missed Days Details', 'Lateness Details', 'Leave Offset History', 'Payroll Impact', 'Warnings and Queries', 'Management Actions', 'Audit Trail']
const ACTIONS = {
  send_payroll: ['Send to Payroll', 'The employee\'s declined missed days and their deductions are sent to payroll for posting.', 'Send to Payroll'],
  send_management: ['Send to Management', 'Every open missed day for this employee is escalated to management for a decision.', 'Escalate'],
  finalise: ['Save as Final', 'The statement is recorded as final. All missed days must already be reviewed.', 'Save as Final'],
  comment: ['Add HR Comment', 'The comment is kept on the employee\'s audit trail.', 'Add Comment'],
}

export default function MissedDaysHistory() {
  const { notify } = useHR()
  const [list, setList] = useState(null)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [chip, setChip] = useState('All')
  const [sel, setSel] = useState(null)
  const [d, setD] = useState(null)
  const [busyEmp, setBusyEmp] = useState(false)
  const [tab, setTab] = useState(TABS[0])
  const [full, setFull] = useState(false)
  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(false)

  const loadList = useCallback(() => hr.mdHistory().then(x => { setList(x); setError(''); return x }).catch(e => setError(e.message)), [])
  const loadEmp = useCallback(id => { setBusyEmp(true); return hr.mdHistoryEmployee(id).then(setD).catch(e => notify(e.message, 'bad')).finally(() => setBusyEmp(false)) }, [notify])
  useEffect(() => { loadList().then(x => { if (x?.rows?.length) setSel(x.rows[0].employee_id) }) }, [loadList])
  useEffect(() => { if (sel) { setD(null); loadEmp(sel) } }, [sel, loadEmp])

  const rows = useMemo(() => {
    if (!list) return []
    const s = q.trim().toLowerCase()
    return list.rows.filter(r => (chip === 'All' || r.status === chip || (chip === 'High Risk' && ['High', 'Critical'].includes(r.risk)))
      && (!s || [r.name, r.employee_no].some(v => String(v || '').toLowerCase().includes(s))))
  }, [list, q, chip])

  if (error && !list) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!list) return <Loader title="Loading employee history…" subtitle="Fetching the employee's records" />

  const refresh = () => Promise.all([loadList(), sel && loadEmp(sel)]).then(() => notify('Employee history refreshed'))
  const run = note => {
    setBusy(true)
    hr.mdHistoryAction(sel, { action: form.action, note })
      .then(r => { notify(r.message || 'Done'); setForm(null); return Promise.all([loadList(), loadEmp(sel)]) })
      .catch(e => notify(e.message, 'bad'))
      .finally(() => setBusy(false))
  }

  const detail = { d, tab, setTab, full, setFull, can: list.can, busy: busy || busyEmp, onAction: a => setForm({ action: a }), onRefresh: refresh }

  return (
    <div className="mdr mds mdh">
      <div className="mdr-top">
        <div>
          <h2>Employee Attendance History &amp; Payroll Impact Center</h2>
          <p>Track missed days, lateness, leave offset, deductions, approvals, and attendance risk per employee.</p>
        </div>
      </div>

      {d && <Detail part="top" {...detail} />}

      <div className="mdh-layout">
        <aside className="mdr-card mdh-list no-print">
          <div className="mdr-search"><Search size={14} /><input placeholder="Search name or ID..." value={q} onChange={e => setQ(e.target.value)} /></div>
          <div className="mdh-chips-label">Filters</div>
          <div className="mdh-chips">
            {['All', ...list.statuses].map(c => (
              <button key={c} className={c === chip ? 'active' : ''} onClick={() => setChip(c)}>
                {c}<span>{c === 'All' ? list.rows.length : list.rows.filter(r => r.status === c || (c === 'High Risk' && ['High', 'Critical'].includes(r.risk))).length}</span>
              </button>
            ))}
          </div>
          <div className="mdh-people">
            {rows.map((r, i) => (
              <button key={r.employee_id} className={`mdh-person ${r.employee_id === sel ? 'active' : ''}`} style={{ animationDelay: `${Math.min(i, 15) * 25}ms` }}
                onClick={() => { setSel(r.employee_id); setTab(TABS[0]); setFull(false) }}>
                <b>{r.name}</b>
                <small>{r.employee_no}{r.designation ? ` · ${r.designation}` : ''}</small>
                <small>{r.department}{r.branch ? ` · ${r.branch}` : ''}</small>
                <div className="mdh-tags"><Chip tone={HIST_TONE[r.status]}>{r.status}</Chip><Chip tone={RISK_TONE[r.risk]}>{`Risk: ${r.risk}`}</Chip></div>
                <div className="mdh-mini">
                  <span><b>{num(r.missed)}</b>Missed</span>
                  <span><b>{num(r.late)}</b>Late</span>
                  <span className={r.deduction ? 'neg' : ''}><b>{fmtMoney(r.deduction)}</b>Ded.</span>
                </div>
                {(r.pending > 0 || r.warnings > 0) && (
                  <div className="mdh-flags">
                    {r.pending > 0 && <span className="p">{num(r.pending)} pending</span>}
                    {r.warnings > 0 && <span className="w">{r.warnings} warning{r.warnings > 1 ? 's' : ''}</span>}
                  </div>
                )}
              </button>
            ))}
            {!rows.length && <div className="mdr-empty">No employees match.</div>}
          </div>
          <div className="mdh-count">{rows.length} employee(s)</div>
        </aside>

        <section className="mdh-detail">
          {!sel && <div className="mdr-card mdr-empty">Select an employee to see their history.</div>}
          {sel && !d && (busyEmp ? <Loader title="Loading employee…" subtitle="Fetching missed days, lateness and leave offsets" /> : <div className="mdr-card mdr-empty">No data.</div>)}
          {d && <Detail part="side" {...detail} />}
        </section>
      </div>

      {form && <ActionModal action={form.action} name={d?.profile.name} busy={busy} onClose={() => setForm(null)} onSubmit={run} />}
    </div>
  )
}

// part 'top': the tabs and their content (shown full width under the page header); part 'side': profile, actions and timeline
function Detail({ part, d, tab, setTab, full, setFull, can, busy, onAction, onRefresh }) {
  const p = d.profile
  const k = d.kpis
  const csv = (name, cols, rows) => download(`${p.employee_no}-${name}.csv`, toCSV(cols, rows))
  const show = t => full || tab === t
  const pdf = () => { document.title = `${p.employee_no} ${p.name} - Missed Days Statement`; window.print() }

  return (
    <div className={`mdh-detail-inner mdh-${part}`}>
      {part === 'top' && (
        <div className="md-kpis mds-kpis">
          {[['Total Missed Days', k.total, CalendarDays, 'blue', 'In record'], ['Approved and Paid', k.approved, CircleCheckBig, 'green', 'No deduction'],
            ['Declined / Deducted', k.declined, CircleX, 'red', 'Salary deduction'], ['Leave Assigned', k.leave_offset, CalendarCheck, 'indigo', 'Charged to leave'],
            ['Total Lateness', d.lateness.length, Clock, 'orange', 'Late clock-ins'], ['Total Mins Late', k.late_minutes, Timer, 'amber', 'Beyond shift start'],
            ['Pending HR Actions', k.pending, Hourglass, 'purple', 'Awaiting review'], ['Total Deduction', fmtMoney(k.deduction), Banknote, 'rose', 'Payroll impact']]
            .map(([label, value, Ico, tone, note], i) => <Kpi key={label} {...{ label, value, Ico, tone, note, i }} />)}
        </div>
      )}
      {part === 'side' && <>
      <div className="mdr-top mdh-head">
        <div>
          <h2>{p.name}</h2>
          <p>{[p.employee_no, p.designation, p.department, p.branch].filter(Boolean).join(' — ')}</p>
          <p>{[p.grade && `Grade ${p.grade}`, p.employment_type, p.supervisor && `Supervisor: ${p.supervisor}`, p.manager && `Manager: ${p.manager}`].filter(Boolean).join(' | ')}</p>
          <p>{[p.site && `Site: ${p.site}`, p.joined && `Joined: ${p.joined}`].filter(Boolean).join(' | ')}</p>
          <div className="mdh-head-tags">
            <Chip tone={HIST_TONE[d.status]}>{d.status}</Chip>
            <span className="mdh-pill">Deduction: {fmtMoney(k.deduction)}</span>
            {k.pending > 0 && <span className="mdh-pill warn">{num(k.pending)} pending</span>}
            {d.final && <span className="mdh-pill ok">Final · {String(d.final.on || '').slice(0, 10)}</span>}
          </div>
        </div>
      </div>

      <div className="mdh-actions no-print">
        <button className={`mdr-btn ${full ? 'on' : ''}`} onClick={() => setFull(f => !f)}><FileText size={14} /> {full ? 'Tabbed View' : 'Full Report'}</button>
        <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print Statement</button>
        <button className="mdr-btn" onClick={pdf} title="Choose 'Save as PDF' in the print window"><FileDown size={14} /> Export PDF</button>
        {can.approve && <>
          <button className="mdr-bulk-btn b-green" disabled={busy} onClick={() => onAction('send_payroll')}><Send size={14} /> Send to Payroll</button>
          <button className="mdr-bulk-btn b-orange" disabled={busy} onClick={() => onAction('send_management')}><BriefcaseBusiness size={14} /> Send to Management</button>
          <button className="mdr-bulk-btn b-blue" disabled={busy} onClick={() => onAction('finalise')}><Save size={14} /> Save as Final</button>
          <button className="mdr-btn" disabled={busy} onClick={() => onAction('comment')}><MessageSquarePlus size={14} /> Add HR Comment</button>
        </>}
        <button className="mdr-btn" disabled={busy} onClick={onRefresh}><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="mdr-card mdh-timeline">
        <div className="mds-table-head">
          <b>Attendance Incident Timeline</b>
          <div className="mdh-tl-sum">
            <span><CalendarX size={13} /> {num(k.total)} Missed Days</span>
            <span><Clock size={13} /> {d.lateness.length} Lateness Cases</span>
            <span className="neg"><Banknote size={13} /> {fmtMoney(k.deduction)} Total Deduction</span>
          </div>
        </div>
        <div className="mdh-tl-list">
          {d.timeline.map((e, i) => (
            <div key={i} className={`mdh-tl-item ${e.kind === 'Lateness' ? 'late' : 'miss'}`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
              <div className="mdh-tl-date"><b>{e.date}</b><span>{e.kind}</span></div>
              <div className="mdh-tl-body">
                <Chip tone={e.kind === 'Lateness' ? 'orange' : STATUS_TONE[e.status]}>{e.status}</Chip>
                <p>{e.detail}</p>
                <small className={e.deduction ? 'neg' : ''}>{e.deduction ? `${fmtMoney(e.deduction)} deduction` : 'No deduction'}</small>
                {e.note && <q>{e.note}</q>}
              </div>
            </div>
          ))}
          {!d.timeline.length && <div className="mdr-empty">No missed days or late clock-ins recorded.</div>}
        </div>
      </div>

      {!full && (
        <div className="mds-tabs no-print" role="tablist">
          {TABS.map(t => <button key={t} type="button" role="tab" className={t === tab ? 'active' : ''} aria-selected={t === tab} onClick={() => setTab(t)}>{t}</button>)}
        </div>
      )}

      <div key={full ? 'full' : tab} className="mds-pane">
        {show('Overview') && (
          <Section title="Overview" full={full}>
            <div className="mdr-card mdh-risk">
              <div className="mdh-risk-head">
                <span><ShieldAlert size={16} /> Attendance Risk Score <em className="mdh-who">— {p.name} ({p.employee_no})</em></span>
                <b className={`r-${d.risk.level.toLowerCase()}`}>{d.risk.level} Risk — {d.risk.score}/100</b>
              </div>
              <div className="mdh-risk-bar"><span className={`r-${d.risk.level.toLowerCase()}`} style={{ width: `${Math.max(d.risk.score, 2)}%` }} /></div>
              <small>Weighs declined days most, then open cases, lateness and warning levels.</small>
            </div>
          </Section>
        )}

        {show('Missed Days Details') && (
          <Section title="Missed Days Details" full={full}>
            <Table
              note={`${d.details.length} missed day record(s)`}
              onExport={can.export ? () => csv('missed-days', [
                { label: 'MD ID', get: r => r.reference }, { label: 'Date', get: r => r.date }, { label: 'Day', get: r => r.weekday }, { label: 'Shift', get: r => r.shift },
                { label: 'Source', get: r => r.source }, { label: 'Reason', get: r => r.reason }, { label: 'Explanation', get: r => r.explanation },
                { label: 'HR Decision', get: r => r.recommendation }, { label: 'Status', get: r => r.status }, { label: 'Paid?', get: r => (r.paid ? 'Yes' : 'No') },
                { label: 'Deduction', get: r => r.deduction || '' }, { label: 'Leave Type', get: r => r.leave_type }, { label: 'Period', get: r => r.payroll_period },
                { label: 'Posting', get: r => r.posting }, { label: 'Action By', get: r => r.action_by },
              ], d.details) : null}
              head={['#', 'MD ID', 'Date', 'Day', 'Shift', 'Source', 'Reason', 'Explanation', 'HR Decision', 'Status', 'Paid?', 'Deduction', 'Leave Type', 'Period', 'Posting', 'Action By']}
              rows={d.details}
              row={(r, i) => [i + 1, <span className="mdr-id">{r.reference}</span>, <span className="nowrap">{r.date}</span>, r.weekday, <span className="nowrap">{r.shift}</span>, r.source,
                <span className="mds-wrap">{r.reason || '—'}</span>, <span className="mds-wrap">{r.explanation || '—'}</span>, r.recommendation || '—',
                <Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip>, <YesNo v={r.paid} />, <span className="mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</span>,
                r.leave_type ? `${r.leave_type} Leave` : '—', <span className="nowrap">{r.payroll_period}</span>, <Chip tone={PAY_TONE[r.posting]}>{r.posting}</Chip>, r.action_by || '—']}
              foot={d.details.length ? [`TOTALS — ${d.details.length} record(s)`, 11, money(d.details.reduce((s, r) => s + (r.deduction || 0), 0)), 4] : null}
            />
          </Section>
        )}

        {show('Lateness Details') && (
          <Section title="Lateness Details" full={full}>
            <Table
              note={`${d.lateness.length} lateness record(s) | ${fmtNumber(k.late_minutes)} total mins`}
              onExport={can.export ? () => csv('lateness', [
                { label: 'Date', get: r => r.date }, { label: 'Day', get: r => r.weekday }, { label: 'Expected In', get: r => r.expected }, { label: 'Actual In', get: r => r.actual },
                { label: 'Mins Late', get: r => r.minutes }, { label: 'Hrs Late', get: r => r.hours }, { label: 'Source', get: r => r.source }, { label: 'Reason', get: r => r.reason },
              ], d.lateness) : null}
              head={['#', 'Date', 'Day', 'Expected In', 'Actual In', 'Mins Late', 'Hrs Late', 'Source', 'Reason / Remark']}
              rows={d.lateness}
              empty="No late clock-ins recorded."
              row={(r, i) => [i + 1, <span className="nowrap">{r.date}</span>, r.weekday, r.expected, <b className="neg">{r.actual}</b>, <b>{r.minutes}</b>, r.hours.toFixed(2), r.source || '—', r.reason || '—']}
              foot={d.lateness.length ? ['TOTALS', 5, fmtNumber(k.late_minutes), 3] : null}
            />
          </Section>
        )}

        {show('Leave Offset History') && (
          <Section title="Leave Offset History" full={full}>
            <Table
              note="Missed days settled against the employee's leave balance instead of a salary deduction."
              head={['MD ID', 'Date', 'Day', 'Days', 'Leave Type', 'Leave ID', 'Leave Status', 'Note', 'Assigned By', 'Assigned On']}
              rows={d.offsets}
              empty="No missed days have been offset against leave."
              row={r => [<span className="mdr-id">{r.reference}</span>, <span className="nowrap">{r.date}</span>, r.weekday, num(r.days),
                <Chip tone="indigo">{r.leave_type ? `${r.leave_type} Leave` : null}</Chip>, r.leave_ref || '—', r.leave_status ? <span className="cap">{r.leave_status}</span> : '—',
                <span className="mds-wrap">{r.note || '—'}</span>, r.by || '—', r.on || '—']}
            />
          </Section>
        )}

        {show('Payroll Impact') && (
          <Section title="Payroll Impact" full={full}>
            <Table
              note="Missed days deductions by payroll month"
              head={['Month', 'Year', 'Days Deducted', 'Daily Rate', 'Missed Deduction', 'Paid Missed', 'Leave Offset', 'Pending', 'Batch', 'Posting Status', 'Posted Date']}
              rows={d.payroll}
              row={r => [r.month, r.year, num(r.deducted_days), <span className="nowrap">{fmtMoney(r.daily_rate)}</span>,
                <span className="mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</span>, num(r.paid_days), num(r.leave_offset), num(r.pending),
                <span className="mdr-id">{r.batch}</span>, <Chip tone={PAY_TONE[r.posting]}>{r.posting}</Chip>, r.posted_on || '—']}
              foot={d.payroll.length ? ['TOTAL DEDUCTION', 4, money(k.deduction), 6] : null}
            />
          </Section>
        )}

        {show('Warnings and Queries') && (
          <Section title="Warnings and Queries" full={full}>
            <div className="mds-timeline">
              {d.warnings.map((w, i) => (
                <div key={i} className="mdr-card mds-event mdh-warn" style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
                  <div className="mds-event-head">
                    <b>{w.date} — {w.kind} <span>· {w.reference}</span></b>
                    <Chip tone={WARN_TONE[w.level]}>{w.level === 'Normal' ? 'No Warning' : w.level}</Chip>
                  </div>
                  <dl className="mdh-warn-grid">
                    {[['Warning Level', w.level], ['Query Type', w.query], ['Issued By', w.issued_by], ['Issued Date', w.issued_on],
                      ['Status', w.status], ['HR Decision', w.hr_decision], ['Mgmt Decision', w.mgmt]].map(([l, v]) => <div key={l}><dt>{l}</dt><dd>{v || '—'}</dd></div>)}
                  </dl>
                  <div className="mdh-warn-text"><b>Reason:</b> {w.reason || '—'}</div>
                  <div className="mdh-warn-text"><b>Employee Response / HR Note:</b> {w.response || '—'}</div>
                </div>
              ))}
              {!d.warnings.length && <div className="mdr-card mdr-empty">No warnings or queries for this employee.</div>}
            </div>
          </Section>
        )}

        {show('Management Actions') && (
          <Section title="Management Actions" full={full}>
            <Table
              head={['Date', 'Action Type', 'Record Type', 'Record', 'Previous Status', 'New Status', 'Action By', 'Role', 'Comment', 'Payroll Effect', 'Audit Ref']}
              rows={d.management}
              empty="No management actions recorded."
              row={a => [<span className="nowrap">{String(a.on || '').replace('T', ' ').slice(0, 16)}</span>, a.action, a.record_type, <span className="mdr-id">{a.record}</span>,
                <Chip tone={STATUS_TONE[a.previous]}>{a.previous}</Chip>, <Chip tone={STATUS_TONE[a.new]}>{a.new}</Chip>, a.user, a.role || '—',
                <span className="mds-wrap">{a.comment || '—'}</span>, a.payroll || '—', <span className="mdr-id">{a.ref}</span>]}
            />
          </Section>
        )}

        {show('Audit Trail') && (
          <Section title="Audit Trail" full={full}>
            <Table
              onExport={can.export ? () => csv('audit-trail', [
                { label: 'Audit Ref', get: a => a.ref }, { label: 'Date and Time', get: a => a.on }, { label: 'User', get: a => a.user }, { label: 'Role', get: a => a.role },
                { label: 'Action', get: a => a.action }, { label: 'Module', get: a => a.module }, { label: 'Record Type', get: a => a.record_type }, { label: 'Record ID', get: a => a.record },
                { label: 'Previous Value', get: a => a.previous }, { label: 'New Value', get: a => a.new }, { label: 'Comment', get: a => a.comment },
              ], d.audit) : null}
              head={['Audit Ref', 'Date and Time', 'User', 'Role', 'Action', 'Module', 'Record Type', 'Record ID', 'Previous Value', 'New Value', 'Comment']}
              rows={d.audit}
              empty="No audit records yet."
              row={a => [<span className="mdr-id">{a.ref}</span>, <span className="nowrap">{String(a.on || '').replace('T', ' ').slice(0, 19)}</span>, a.user, a.role || '—', a.action,
                a.module, a.record_type, <span className="mdr-id">{a.record}</span>, a.previous || '—', a.new || '—', <span className="mds-wrap">{a.comment || '—'}</span>]}
            />
          </Section>
        )}
      </div>
      </>}
    </div>
  )
}

// in Full Report every section is shown one after the other, each with its heading
const Section = ({ title, full, children }) => (full ? <div className="mdh-section"><h3 className="mdh-section-title">{title}</h3>{children}</div> : <>{children}</>)

function ActionModal({ action, name, busy, onClose, onSubmit }) {
  const [note, setNote] = useState('')
  const [title, intro, label] = ACTIONS[action]
  const need = action === 'comment'
  return (
    <Modal title={`${title} — ${name}`} onClose={onClose}>
      <form className="mdr-form" onSubmit={e => { e.preventDefault(); onSubmit(note) }}>
        <p className="muted small">{intro}</p>
        <label className="mdh-field">{need ? 'Comment' : 'Note (optional)'}
          <textarea rows={4} value={note} autoFocus required={need} onChange={e => setNote(e.target.value)} placeholder={need ? 'Write the HR comment…' : 'Add a note for the record…'} />
        </label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy || (need && !note.trim())}>{busy ? 'Saving…' : label}</button>
        </div>
      </form>
    </Modal>
  )
}
